/**
 * Sync Status Indicator
 * 
 * Shows the current sync status between local storage and Supabase.
 * Displays warnings when data is only stored locally.
 */

import { useState, useEffect } from 'react';
import { Cloud, CloudOff, AlertTriangle } from 'lucide-react';
import { isSupabaseAvailable } from '../../lib/supabaseSync';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useEnterpriseIdentityStore } from '../../stores/prioritization/enterpriseIdentityStore';

type SyncStatus = 'synced' | 'local-only' | 'syncing' | 'error';

export function SyncStatusIndicator() {
  const [status, setStatus] = useState<SyncStatus>('synced');
  const [message, setMessage] = useState<string>('');
  const [showDetails, setShowDetails] = useState(false);
  
  const candidatesSyncState = useItemsStore((state) => state.syncState);
  const enterpriseSyncState = useEnterpriseIdentityStore((state) => state.supabaseSyncState);
  
  useEffect(() => {
    // Check if Supabase is available
    if (!isSupabaseAvailable()) {
      setStatus('local-only');
      setMessage('Supabase not configured. Data stored locally only.');
      return;
    }
    
    // Check for sync errors
    if (candidatesSyncState.syncError || enterpriseSyncState.syncError) {
      setStatus('error');
      setMessage(candidatesSyncState.syncError || enterpriseSyncState.syncError || 'Sync error');
      return;
    }
    
    // Check if syncing
    if (candidatesSyncState.isLoading || enterpriseSyncState.isLoading) {
      setStatus('syncing');
      setMessage('Syncing with cloud...');
      return;
    }
    
    // All good
    setStatus('synced');
    setMessage('Data synced with cloud');
  }, [candidatesSyncState, enterpriseSyncState]);
  
  const getStatusIcon = () => {
    switch (status) {
      case 'synced':
        return <Cloud className="h-4 w-4 text-[var(--green)]" />;
      case 'local-only':
        return <CloudOff className="h-4 w-4 text-[var(--mustard)]" />;
      case 'syncing':
        return <Cloud className="h-4 w-4 text-[var(--text-ter)] animate-pulse" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-[var(--coral)]" />;
    }
  };
  
  const getStatusColor = () => {
    switch (status) {
      case 'synced':
        return 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]';
      case 'local-only':
        return 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]';
      case 'syncing':
        return 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]';
      case 'error':
        return 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)]';
    }
  };
  
  // Only surface persistent problems (error / local-only). Normal syncing is routine —
  // rendering a button during every fetch causes the header to shift on every page load.
  if (status === 'synced' || status === 'syncing') {
    return null;
  }
  
  return (
    <div className="relative">
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={`flex items-center gap-1.5 px-2 py-1  border text-xs ${getStatusColor()}`}
        title={message}
      >
        {getStatusIcon()}
        <span className="hidden sm:inline">
          {status === 'local-only' && 'Local Only'}
          {status === 'syncing' && 'Syncing...'}
          {status === 'error' && 'Sync Error'}
        </span>
      </button>
      
      {showDetails && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setShowDetails(false)} 
          />
          <div className="absolute right-0 top-full mt-1 w-64 bg-[var(--bg-raised)] shadow-lg border py-2 z-20">
            <div className="px-3 py-2 border-b">
              <div className="flex items-center gap-2">
                {getStatusIcon()}
                <span className="font-medium text-[var(--text-pri)]">Sync Status</span>
              </div>
            </div>
            
            <div className="px-3 py-2 text-xs text-[var(--text-sec)]">
              <p>{message}</p>
              
              {status === 'local-only' && (
                <p className="mt-2 text-[var(--mustard)]">
                  Your changes are saved locally but won't sync across devices.
                </p>
              )}
              
              {status === 'error' && (
                <p className="mt-2 text-[var(--coral)]">
                  Some data may not have synced. Check the console for details.
                </p>
              )}
              
              {candidatesSyncState.lastSyncAt && (
                <p className="mt-2 text-[var(--text-ter)]">
                  Last sync: {new Date(candidatesSyncState.lastSyncAt).toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
